import React, { useState, useEffect } from 'react';
import { Toaster, toast } from 'sonner';
import { Upload, Search, Filter, Phone, RefreshCw, Edit, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import ActionButton from '../../components/ActionButton';

// Helper function to format IST time
const formatISTTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  try {
    const date = new Date(dateTimeString);
    const istTime = new Date(date.getTime() + (5.5 * 60 * 60 * 1000)); // UTC + 5:30
    return istTime.toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateTimeString;
  }
};

const CampaignManagement = () => {
  const [showNewFormModal, setShowNewFormModal] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
const [amount, setAmount] = useState('');
const [dueDate, setDueDate] = useState('');
const [callStatus, setCallStatus] = useState('');
const [others, setOthers] = useState('');
const handleEditCampaign = async (mobileNumber) => {
  console.log('Edit campaign with Mobile Number:', mobileNumber);
  try {
    const userId = localStorage.getItem('userId');
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/campaign-by-mobile?mobileNumber=${mobileNumber}&userId=${userId}`);
    if (response.ok) {
      const campaignData = await response.json();
      console.log('Fetched campaign data:', campaignData);

      // Populate form fields with fetched data
      setMobileNumber(campaignData.campaign.MobileNumber?.S || '');
      setFirstName(campaignData.campaign.FirstName?.S || '');
      setLastName(campaignData.campaign.LastName?.S || '');
      setAmount(campaignData.campaign.Amount?.S || '');
      setDueDate(campaignData.campaign.DueDate?.S || '');
      setCallStatus(campaignData.campaign.CallStatus?.S || '');
      setOthers(campaignData.campaign.Others?.S || '');
      
      // Set edit mode and store original mobile number
      setIsEditMode(true);
      setOriginalMobileNumber(campaignData.campaign.MobileNumber?.S || '');
      
      console.log('State updated with campaign data:', {
        amount: campaignData.campaign.Amount?.S || '',
        dueDate: campaignData.campaign.DueDate?.S || '',
        callStatus: campaignData.campaign.CallStatus?.S || '',
        others: campaignData.campaign.Others?.S || ''
      });
      setShowNewFormModal(true); // Show the form modal
    } else {
      console.error('Failed to fetch campaign data:', response.status);
      toast.error('Failed to fetch campaign data.');
    }
  } catch (error) {
    console.error('Error fetching campaign data:', error);
    toast.error('An error occurred while fetching campaign data.');
  }
};

const handleDeleteCampaign = async (mobileNumber, userId) => {
  userId = userId || localStorage.getItem('userId');
  if (!userId) {
    toast.error('User ID is missing. Please log in again.');
    console.error('Error: User ID is undefined.');
    return;
  }
  try {
    const requestUrl = `${process.env.REACT_APP_API_BASE_URL}/api/campaign?mobileNumber=${mobileNumber}&userId=${userId}`;
    console.log('DELETE Request URL:', requestUrl);
    const response = await fetch(requestUrl, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Campaign deleted successfully:', result);
      toast.success('Campaign deleted successfully');
      fetchAllCampaigns(); // Refresh campaigns list
    } else {
      const error = await response.json();
      console.error('Failed to delete campaign:', error);
      toast.error('Failed to delete campaign');
    }
  } catch (error) {
    console.error('Error deleting campaign:', error);
    toast.error('An error occurred while deleting the campaign');
  }
};
  const [campaigns, setCampaigns] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [campaignsPerPage] = useState(5);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalMobileNumber, setOriginalMobileNumber] = useState('');

  // Add new campaign functionality
  const handleAddCampaign = () => {
    const newEmptyCampaign = {
      id: 'new-' + Date.now(), // Temporary ID
      srNo: campaigns.length + 1,
      mobileNumber: '',
      firstName: '',
      lastName: '',
      amount: '',
      dueDate: '',
      callStatus: 'pending',
      others: '',
      isNewRow: true
    };
    setCampaigns([newEmptyCampaign, ...campaigns]);
  };

  const handleNewCampaignChange = (campaignId, field, value) => {
    setCampaigns(prevCampaigns =>
      prevCampaigns.map(campaign =>
        campaign.id === campaignId ? { ...campaign, [field]: value } : campaign
      )
    );
  };

  const handleSaveNewCampaign = async (campaign) => {
    try {
      const userId = localStorage.getItem('userId');
      const campaignData = {
        userId,
        mobileNumber: campaign.mobileNumber,
        firstName: campaign.firstName,
        lastName: campaign.lastName,
        amount: campaign.amount,
        dueDate: campaign.dueDate,
        callStatus: campaign.callStatus,
        others: campaign.others,
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/campaign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(campaignData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Campaign created successfully:', result);
        toast.success('Campaign created successfully!');
        await fetchAllCampaigns(); // Refresh the campaign list
      } else {
        const error = await response.json();
        console.error('Error creating campaign:', error);
        toast.error('Failed to create campaign.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add campaign';
      toast.error(errorMessage);
    }
  };

  const handleCancelNewCampaign = (campaignId) => {
    setCampaigns(prevCampaigns => prevCampaigns.filter(c => c.id !== campaignId));
  };

  // No sample data - everything comes from backend

  useEffect(() => {
    // Fetch available clients, agents and all campaigns on component load
    fetchAvailableClients();
    fetchAvailableAgents();
    fetchAllCampaigns();
  }, []);

  // Set defaults when data is loaded
  useEffect(() => {
    if (availableClients.length > 0 && !selectedClient) {
      setSelectedClient(availableClients[0]);
    }
  }, [availableClients, selectedClient]);

  useEffect(() => {
    if (availableAgents.length > 0 && !selectedAgent) {
      setSelectedAgent(availableAgents[0].agent_id);
    }
  }, [availableAgents, selectedAgent]);
  
  useEffect(() => {
    if (!scheduleDateTime) {
      // Set default to current time + 1 hour in IST
      const now = new Date();
      // Convert to IST (UTC + 5:30)
      const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
      istTime.setHours(istTime.getHours() + 1);
      istTime.setMinutes(0);
      istTime.setSeconds(0);
      setScheduleDateTime(istTime.toISOString().slice(0, 16));
    }
  }, [scheduleDateTime]);

  const fetchAvailableClients = async () => {
    try {
      const now = new Date();
      const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
      console.log('🔄 Fetching available clients...', istTime.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://tubelight-backend.shashank-40f.workers.dev'}/api/campaigns/clients`);
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Clients response:', result);
        if (result.success) {
          setAvailableClients(result.data);
          console.log('📋 Set available clients:', result.data);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching clients:', error);
      setAvailableClients([]);
    }
  };

  const handleAddClient = async () => {
    if (!newClientName.trim()) {
      toast.error('Please enter a client name');
      return;
    }

    setIsAddingClient(true);
    try {
      const requestBody = {
        name: newClientName.trim()
      };
      console.log('Sending client creation request:', requestBody);
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://tubelight-backend.shashank-40f.workers.dev'}/api/campaigns/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      if (response.ok) {
        const result = await response.json();
        console.log('Response data:', result);
        if (result.success) {
          toast.success('Client added successfully!');
          setNewClientName('');
          setShowAddClientModal(false);
          // Refresh the clients list
          await fetchAvailableClients();
          // Set the new client as selected
          setSelectedClient(newClientName.trim());
        } else {
          toast.error(result.error || 'Failed to add client');
        }
      } else {
        toast.error('Failed to add client');
      }
    } catch (error) {
      console.error('Error adding client:', error);
      toast.error('Failed to add client');
    } finally {
      setIsAddingClient(false);
    }
  };

  const fetchAvailableAgents = async () => {
    try {
      const now = new Date();
      const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
      console.log('🔄 Fetching available agents...', istTime.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://tubelight-backend.shashank-40f.workers.dev'}/api/agents`);
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Agents response:', result);
        if (result.success) {
          setAvailableAgents(result.data);
          console.log('📋 Set available agents:', result.data);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching agents:', error);
      setAvailableAgents([]);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    console.log('File selected:', file.name, 'Size:', file.size, 'Type:', file.type);

    // Validate file type
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isValidType = allowedTypes.includes(file.type) || ['csv', 'xls', 'xlsx'].includes(fileExtension);
    
    if (!isValidType) {
      toast.error('Please upload CSV, XLS, or XLSX files only');
      event.target.value = ''; // Clear file input
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    // Check if all required fields are selected
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }

    if (!selectedAgent) {
      toast.error('Please select an agent first');
      return;
    }

    if (!scheduleDateTime) {
      toast.error('Please select schedule date and time first');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('clientId', selectedClient);
      formData.append('agentId', selectedAgent);
      formData.append('scheduleDateTime', scheduleDateTime);

      // Simulate progress (in real implementation, use WebSocket or SSE)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://tubelight-backend.shashank-40f.workers.dev'}/api/campaigns/import`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      
              if (result.success) {
          toast.success(`Successfully processed ${result.data.successCount} campaigns from ${selectedFile.name}`);
          
          // Refresh campaign list - fetch all campaigns since we cleared selectedClient
          await fetchAllCampaigns();
        
        setShowUploadModal(false);
        setSelectedClient('');
        setSelectedAgent('');
        setScheduleDateTime('');
        setSelectedFile(null);
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  /* const fetchCampaignsByClient = async (clientId) => {
    try {
      setIsLoadingCampaigns(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://tubelight-backend.shashank-40f.workers.dev'}/api/campaigns/customers/${clientId}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Transform backend data to frontend format
          const transformedCampaigns = result.campaigns.map((campaign, index) => ({
            id: campaign.userId?.S || 'N/A',
            srNo: index + 1,
            mobileNumber: campaign.MobileNumber?.S || 'N/A',
            firstName: campaign.FirstName?.S || 'N/A',
            lastName: campaign.LastName?.S || 'N/A',
            amount: campaign.Amount?.S || 'N/A',
            dueDate: campaign.DueDate?.S || 'N/A',
            callStatus: campaign.CallStatus?.S || 'N/A',
            others: campaign.Others?.S || 'N/A',
            actions: 'Edit'
          }));
          setCampaigns(transformedCampaigns);
        }
      } else {
        console.error('Failed to fetch campaigns for client:', response.status);
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setIsLoadingCampaigns(false);
    }
  }; */

  const fetchAllCampaigns = async () => {
    try {
      setIsLoadingCampaigns(true);
      const userId = localStorage.getItem('userId');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/campaign?userId=${userId}`);
      if (response.ok) {
        const result = await response.json();
        if (result.campaigns) {
          // Transform backend data to frontend format
          const transformedCampaigns = result.campaigns.map((campaign, index) => ({
            id: campaign.userId?.S || 'N/A',
            srNo: index + 1,
            mobileNumber: campaign.MobileNumber?.S || 'N/A',
            firstName: campaign.FirstName?.S || 'N/A',
            lastName: campaign.LastName?.S || 'N/A',
            amount: campaign.Amount?.S || 'N/A',
            dueDate: campaign.DueDate?.S || 'N/A',
            callStatus: campaign.CallStatus?.S || 'N/A',
            others: campaign.Others?.S || 'N/A',
            actions: 'Edit'
          }));
          setCampaigns(transformedCampaigns);
        }
      } else {
        console.error('Failed to fetch campaigns:', response.status);
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error fetching all campaigns:', error);
      setCampaigns([]);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const handleClientSelect = (clientId) => {
    setSelectedClient(clientId);
    setShowUploadModal(true);
//    fetchCampaignsByClient(clientId);
  };

  const handleCreateNewClient = async () => {
    if (newClientName.trim()) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://tubelight-backend.shashank-40f.workers.dev'}/api/campaigns/clients`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: newClientName.trim() })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // Refresh the client list
            await fetchAvailableClients();
            setSelectedClient(result.data.clientId);
            setNewClientName('');
            setShowAddClientModal(false);
            toast.success(`Client "${result.data.name}" created successfully!`);
          }
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create client');
        }
      } catch (error) {
        console.error('Error creating client:', error);
        toast.error(`Failed to create client: ${error.message}`);
      }
    }
  };

  const handleUpdateCallStatus = async (mobileNumber, newStatus) => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        toast.error('User ID is missing. Please log in again.');
        return;
      }

      // Update the campaign with new call status using the correct API format
      const updateData = {
        mobileNumber,
        userId,
        updates: {
          CallStatus: newStatus
        }
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/campaign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Campaign status updated successfully:', result);
        toast.success('Call status updated successfully');
        // Refresh the campaign list
        await fetchAllCampaigns();
      } else {
        const error = await response.json();
        console.error('Failed to update campaign status:', error);
        toast.error('Failed to update call status');
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update call status. Please try again.');
    }
  };

  const handleConfirmDelete = async () => {
    if (campaignToDelete) {
      await handleDeleteCampaign(campaignToDelete.mobileNumber, campaignToDelete.userId);
      setDeleteConfirmOpen(false);
      setCampaignToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setCampaignToDelete(null);
  };

  // Filter and search campaigns
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = (campaign.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (campaign.loanAccountNumber?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (campaign.mobileNumber || '').includes(searchTerm);
    
    // Normalize status comparison (handle case differences)
    const campaignStatus = campaign.callStatus?.toLowerCase() || 'pending';
    const filterStatusLower = filterStatus.toLowerCase();
    const matchesFilter = filterStatus === 'all' || campaignStatus === filterStatusLower;
    
    return matchesSearch && matchesFilter;
  });

  // Pagination
  const indexOfLastCampaign = currentPage * campaignsPerPage;
  const indexOfFirstCampaign = indexOfLastCampaign - campaignsPerPage;
  const currentCampaigns = filteredCampaigns.slice(indexOfFirstCampaign, indexOfLastCampaign);
  const totalPages = Math.ceil(filteredCampaigns.length / campaignsPerPage);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" className='mt-24' />

      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Campaigns</h1>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
            <ActionButton
              text="Add Campaign"
              onClick={handleAddCampaign}
            />
            <ActionButton
              text="Import File"
              onClick={() => setShowUploadModal(true)}
            />
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, loan account, or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Clients:</span>
              <select
                value={selectedClient}
                onChange={(e) => {
                  const client = e.target.value;
                  setSelectedClient(client);
                  if (client) {
                    // fetchCampaignsByClient(client);
                  } else {
                    // If "All Clients" is selected, fetch all campaigns
                    fetchAllCampaigns();
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Clients</option>
                {availableClients.map((client) => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>
          </div>
        </div>
      </div>

      {/* New Form Modal */}
      {showNewFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-lg mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Fill Details
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide the following details:
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number:</label>
              <input
                type="text"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                placeholder="Enter mobile number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="mb-4 flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name:</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name:</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount:</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="mb-4 flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Due Date:</label>
<input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                placeholder="Select due date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Status:</label>
                <select
                  value={callStatus}
                  onChange={(e) => setCallStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="" disabled>Select Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Others:</label>
              <textarea
                value={others}
                onChange={(e) => setOthers(e.target.value)}
                placeholder="Enter other details"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              ></textarea>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNewFormModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const userId = localStorage.getItem('userId');
                    
                    if (isEditMode) {
                      // Update existing campaign
                      const updateData = {
                        mobileNumber: originalMobileNumber, // Use original mobile number for identification
                        userId,
                        updates: {
                          MobileNumber: mobileNumber,
                          FirstName: firstName,
                          LastName: lastName,
                          Amount: amount,
                          DueDate: dueDate,
                          CallStatus: callStatus,
                          Others: others
                        }
                      };

                      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/campaign`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(updateData),
                      });

                      if (response.ok) {
                        const result = await response.json();
                        console.log('Campaign updated successfully:', result);
                        toast.success('Campaign updated successfully!');
                        await fetchAllCampaigns(); // Refresh the campaign list
                      } else {
                        const error = await response.json();
                        console.error('Error updating campaign:', error);
                        toast.error('Failed to update campaign.');
                      }
                    } else {
                      // Create new campaign
                      const campaignData = {
                        userId,
                        mobileNumber,
                        firstName,
                        lastName,
                        amount,
                        dueDate,
                        callStatus,
                        others,
                      };

                      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/campaign`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(campaignData),
                      });

                      if (response.ok) {
                        const result = await response.json();
                        console.log('Campaign created successfully:', result);
                        toast.success('Campaign created successfully!');
                        await fetchAllCampaigns(); // Refresh the campaign list
                      } else {
                        const error = await response.json();
                        console.error('Error creating campaign:', error);
                        toast.error('Failed to create campaign.');
                      }
                    }
                  } catch (error) {
                    console.error('Error:', error);
                    toast.error(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the campaign.`);
                  } finally {
                    setShowNewFormModal(false);
                    setIsEditMode(false);
                    setOriginalMobileNumber('');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {isEditMode ? 'Update' : 'Submit'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

        {/* Campaign Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr. No.</th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loan Account Number</th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Name</th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Call Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Others</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoadingCampaigns ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex justify-center items-center">
                        <svg className="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="ml-2">Loading campaigns...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // Always show exactly 5 rows
                  Array.from({ length: 5 }, (_, index) => {
                    const campaign = currentCampaigns[index];
                    const isEmptyRow = !campaign;
                    
                    return (
                      <motion.tr
                        key={campaign ? `${campaign.id}-${index}` : `empty-${index}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-gray-50 transition-colors duration-150 h-16"
                        style={{ height: '64px' }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isEmptyRow ? '\u00A0' : (campaign.srNo || 'N/A')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isEmptyRow ? '\u00A0' : (
                            campaign.isNewRow ? (
                              <input
                                type="text"
                                value={campaign.mobileNumber}
                                onChange={(e) => handleNewCampaignChange(campaign.id, 'mobileNumber', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Mobile Number"
                              />
                            ) : (campaign.mobileNumber || 'N/A')
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {isEmptyRow ? '\u00A0' : (
                            campaign.isNewRow ? (
                              <input
                                type="text"
                                value={campaign.firstName}
                                onChange={(e) => handleNewCampaignChange(campaign.id, 'firstName', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="First Name"
                              />
                            ) : (campaign.firstName || 'N/A')
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {isEmptyRow ? '\u00A0' : (
                            campaign.isNewRow ? (
                              <input
                                type="text"
                                value={campaign.lastName}
                                onChange={(e) => handleNewCampaignChange(campaign.id, 'lastName', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Last Name"
                              />
                            ) : (campaign.lastName || 'N/A')
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isEmptyRow ? '\u00A0' : (
                            campaign.isNewRow ? (
                              <input
                                type="number"
                                value={campaign.amount}
                                onChange={(e) => handleNewCampaignChange(campaign.id, 'amount', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Amount"
                              />
                            ) : `₹${campaign.amount ? campaign.amount.toLocaleString() : '0'}`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isEmptyRow ? '\u00A0' : (
                            campaign.isNewRow ? (
                              <input
                                type="date"
                                value={campaign.dueDate}
                                onChange={(e) => handleNewCampaignChange(campaign.id, 'dueDate', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            ) : (
                              campaign.dueDate ? (
                                (() => {
                                  try {
                                    // If it's already a proper date string, format it
                                    if (campaign.dueDate.includes('-')) {
                                      return new Date(campaign.dueDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
                                    }
                                    // If it's a number (Excel serial), convert it
                                    const dateNum = parseInt(campaign.dueDate);
                                    if (!isNaN(dateNum) && dateNum > 1 && dateNum < 100000) {
                                      const excelDate = new Date((dateNum - 25569) * 86400 * 1000);
                                      return excelDate.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
                                    }
                                    return campaign.dueDate;
                                  } catch (e) {
                                    return campaign.dueDate;
                                  }
                                })()
                              ) : 'N/A'
                            )
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEmptyRow ? '\u00A0' : (
                            campaign.isNewRow ? (
                              <select
                                value={campaign.callStatus}
                                onChange={(e) => handleNewCampaignChange(campaign.id, 'callStatus', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="failed">Failed</option>
                              </select>
                            ) : (
                              <select
                                value={campaign.callStatus || 'pending'}
                                onChange={(e) => handleUpdateCallStatus(campaign.mobileNumber, e.target.value)}
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  (campaign.callStatus?.toLowerCase() || 'pending') === 'completed' ? 'bg-green-100 text-green-800' :
                                  (campaign.callStatus?.toLowerCase() || 'pending') === 'failed' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="failed">Failed</option>
                              </select>
                            )
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isEmptyRow ? '\u00A0' : (
                            campaign.isNewRow ? (
                              <input
                                type="text"
                                value={campaign.others}
                                onChange={(e) => handleNewCampaignChange(campaign.id, 'others', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Others"
                              />
                            ) : (campaign.others || 'N/A')
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isEmptyRow ? '\u00A0' : (
                            campaign.isNewRow ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleSaveNewCampaign(campaign)}
                                  className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50 transition-colors duration-200"
                                  title="Save Campaign"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleCancelNewCampaign(campaign.id)}
                                  className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                                  title="Cancel"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-3">
                                <button
                                  onClick={() => handleEditCampaign(campaign.mobileNumber)}
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 transition-colors duration-200"
                                  title="Edit Campaign"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setCampaignToDelete({ mobileNumber: campaign.mobileNumber, userId: campaign.userId });
                                    setDeleteConfirmOpen(true);
                                  }}
                                  className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                                  title="Delete Campaign"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )
                          )}
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <div className="flex justify-center items-center space-x-4">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  title="Previous page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Current Page Number */}
                <div className="flex items-center justify-center min-w-[60px] h-10 px-4 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 font-medium">
                  {currentPage}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  title="Next page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload File Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-lg mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Import Campaigns
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              File should contain: Sr. No., Loan Account Number, Mobile Number, Name, Bank Name, Amount, Due Date
            </p>

            {/* Agent Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Agent:</label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Agent</option>
                {availableAgents.map((agent) => (
                  <option key={agent.agent_id} value={agent.agent_id}>{agent.name}</option>
                ))}
              </select>
            </div>

            {/* Client Management */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Client:</label>
              <div className="flex gap-2">
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Client</option>
                  {availableClients.map((client) => (
                    <option key={client} value={client}>{client}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddClientModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                >
                  + Add Client
                </button>
              </div>
            </div>

            {/* Schedule Date/Time */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Date & Time (IST):</label>
              <input
                type="datetime-local"
                value={scheduleDateTime}
                onChange={(e) => setScheduleDateTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">All times are in Indian Standard Time (IST)</p>
            </div>
            
            {/* Progress Bar */}
            {isUploading && (
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-1">{uploadProgress}% Complete</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select File:</label>
              <input
                type="file"
                name="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileSelect}
                disabled={isUploading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500 mt-1">Supported formats: CSV, XLS, XLSX</p>
              
              {/* Show selected file name */}
              {selectedFile && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    <strong>Selected:</strong> {selectedFile.name}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowUploadModal(false)}
                disabled={isUploading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFileUpload}
                disabled={isUploading || !selectedFile || !selectedClient || !selectedAgent || !scheduleDateTime}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? 'Uploading...' : 'Upload File'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Client</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Name:</label>
              <input
                type="text"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="Enter client name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isAddingClient}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddClientModal(false);
                  setNewClientName('');
                }}
                disabled={isAddingClient}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddClient}
                disabled={isAddingClient || !newClientName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAddingClient ? 'Adding...' : 'Add Client'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Do you want to delete this campaign?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CampaignManagement;
